// Simulador de webhooks — dispara un evento REAL hacia el webhook_url del
// partner (mismo pipeline EventBridge → SQS → webhook-delivery que un evento
// de negocio genuino), marcado `simulated: true` en el detail.
//
// Server-side (services/inspector/actions/handler.ts + webhook-delivery)
// agrega el header `X-AuthSys-Signature`-sibling `X-AuthSys-Simulated: true`
// y loguea con flag `simulated` — así el integrador puede diferenciarlo de un
// evento real sin ambigüedad. A diferencia de ActionsPanel, esto NO muta el
// estado de la cuenta (por eso vive en un panel separado, con su propia
// paleta, para que nunca se confunda con las acciones que sí fuerzan estado).

import { useEffect, useRef, useState } from 'react';
import {
  Webhook,
  CircleCheck,
  RefreshCw,
  CalendarX2,
  ArrowLeftRight,
  Hourglass,
  UserX,
  Tv,
  Radio,
} from 'lucide-react';
import { runAction, type SimulatableWebhookEvent } from '@/lib/api';
import { useI18n } from '@/lib/i18n';
import { StatusLed } from './StatusLed';

const EVENTS: { event: SimulatableWebhookEvent; icon: typeof Webhook }[] = [
  { event: 'subscription.activated', icon: CircleCheck },
  { event: 'subscription.renewed', icon: RefreshCw },
  { event: 'subscription.expired', icon: CalendarX2 },
  { event: 'subscription.changed', icon: ArrowLeftRight },
  { event: 'subscription.grace_period', icon: Hourglass },
  { event: 'user.deactivated', icon: UserX },
  { event: 'device.authorized', icon: Tv },
  { event: 'webhook.ping', icon: Radio },
];

const ARM_TIMEOUT_MS = 4000;

export function WebhookSimPanel({ className = 'col-span-12' }: { className?: string } = {}) {
  const { t, locale } = useI18n();
  const [armed, setArmed] = useState<SimulatableWebhookEvent | null>(null);
  const [busy, setBusy] = useState<SimulatableWebhookEvent | null>(null);
  const [log, setLog] = useState<{ ok: boolean; text: string; at: string }[]>([]);
  const disarmTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => () => {
    if (disarmTimer.current) clearTimeout(disarmTimer.current);
  }, []);

  function arm(event: SimulatableWebhookEvent) {
    setArmed(event);
    if (disarmTimer.current) clearTimeout(disarmTimer.current);
    disarmTimer.current = setTimeout(() => setArmed(null), ARM_TIMEOUT_MS);
  }

  async function fire(event: SimulatableWebhookEvent) {
    setArmed(null);
    setBusy(event);
    const at = new Date().toLocaleTimeString(locale, { hour12: false });
    try {
      const result = await runAction('webhook_simulate', { webhookEvent: event });
      setLog((l) =>
        [
          {
            ok: true,
            text: t('wh.r.sent', { event, id: String(result['event_id'] ?? '?') }),
            at,
          },
          ...l,
        ].slice(0, 6),
      );
    } catch (err) {
      setLog((l) =>
        [
          { ok: false, text: `${t(`wh.${event}`)}: ${err instanceof Error ? err.message : 'error'}`, at },
          ...l,
        ].slice(0, 6),
      );
    } finally {
      setBusy(null);
    }
  }

  return (
    <section
      aria-label={t('t.webhookSim')}
      className={`${className} rounded border border-brand/30 bg-surface`}
    >
      <header className="flex flex-wrap items-center justify-between gap-x-2 gap-y-0.5 border-b border-brand/30 bg-surface-raised px-3 py-1.5">
        <h2 className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.15em] text-brand">
          <StatusLed tone="info" />
          {t('t.webhookSim')} — {t('wh.subtitle')}
        </h2>
        <span className="text-[10px] text-ink-subtle">{t('wh.help')}</span>
      </header>
      <div className="flex flex-wrap gap-2 p-3">
        {EVENTS.map(({ event, icon: Icon }) => {
          const label = t(`wh.${event}`);
          const isArmed = armed === event;
          const isBusy = busy === event;
          return (
            <button
              key={event}
              type="button"
              title={t(`wh.${event}.hint`)}
              disabled={busy !== null}
              onClick={() => (isArmed ? void fire(event) : arm(event))}
              className={`flex min-h-[2.25rem] items-center gap-1.5 rounded border px-2.5 py-1 text-[11px] uppercase tracking-wider transition-colors disabled:opacity-40 ${
                isArmed
                  ? 'border-brand bg-brand/15 text-brand'
                  : 'border-surface-border-strong text-ink-muted hover:border-brand hover:text-brand'
              }`}
            >
              <Icon className="h-3.5 w-3.5" aria-hidden="true" />
              {isBusy ? t('wh.sending') : isArmed ? t('wh.confirm', { label: label.toLowerCase() }) : label}
            </button>
          );
        })}
      </div>
      {log.length > 0 && (
        <ul className="border-t border-surface-border px-3 py-2 text-[11px]" aria-live="polite">
          {log.map((entry, i) => (
            <li key={`${entry.at}-${i}`} className="flex items-center gap-2 py-0.5">
              <StatusLed tone={entry.ok ? 'success' : 'error'} />
              <span className="tabular-nums text-ink-subtle">{entry.at}</span>
              <span className={entry.ok ? 'text-ink-muted' : 'text-status-error'}>{entry.text}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
